/**
 * Refresh-token storage. On native this is the OS keychain (expo-secure-store);
 * the browser has no equivalent, so the token lives in localStorage.
 *
 * That is a deliberate, documented trade-off: any XSS on this origin can read
 * it. The mitigations that matter here are (a) the access token is still kept
 * in memory only, (b) the refresh token rotates on every use server-side, and
 * (c) a strict CSP on the deployed origin. If the API ever grows a
 * `Set-Cookie`-based refresh flow (httpOnly + SameSite=Strict), this module is
 * the single place that changes.
 */
const REFRESH_KEY = 'sagesse.refresh_token';

/** localStorage throws in some privacy modes — never let that break boot. */
function safeStorage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    const probe = '__sagesse_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

const store = safeStorage();

/** In-memory fallback so a blocked-storage browser still completes one session. */
let memoryToken: string | null = null;

export async function saveRefreshToken(token: string): Promise<void> {
  if (store) store.setItem(REFRESH_KEY, token);
  else memoryToken = token;
}

export async function getRefreshToken(): Promise<string | null> {
  if (store) return store.getItem(REFRESH_KEY);
  return memoryToken;
}

export async function clearRefreshToken(): Promise<void> {
  if (store) store.removeItem(REFRESH_KEY);
  memoryToken = null;
}
